import { CartItem, NutritionalInfo } from '@open-tender/types'
import { calcNutrition } from '@open-tender/utils'

function isRemoval(name: any): boolean {
  return typeof name === 'string' && name.toLowerCase().startsWith('remove')
}

function negateNutritionalInfo(info: any): any {
  if (!info) return info
  const result: any = {}
  for (const key of Object.keys(info)) {
    const val = info[key]
    result[key] = typeof val === 'number' ? -val : val
  }
  return result
}

// Sum cals of ALL removal options (regardless of selection). Removal ingredients
// are present by default, so their calories are added to the base; selecting
// "Remove X" then subtracts via correctGroupCals. Removals are 0/1 toggles.
function sumAllRemovalCals(groups: any[]): number {
  let total = 0
  ;(groups || []).forEach(g => {
    ;(g.options || []).forEach((opt: any) => {
      if (isRemoval(opt.name) && opt.cals !== null && opt.cals !== undefined) {
        total += opt.cals
      }
      total += sumAllRemovalCals(opt.groups || [])
    })
  })
  return total
}

// Per-nutrient sum of ALL removal options' nutritionalInfo, added to the base
// so the nutrition panel reflects removables as present by default.
function sumAllRemovalNutrition(groups: any[]): Record<string, number> {
  const acc: Record<string, number> = {}
  ;(groups || []).forEach(g => {
    ;(g.options || []).forEach((opt: any) => {
      if (isRemoval(opt.name) && opt.nutritionalInfo) {
        for (const key of Object.keys(opt.nutritionalInfo)) {
          const val = opt.nutritionalInfo[key]
          if (typeof val === 'number') acc[key] = (acc[key] || 0) + val
        }
      }
      const nested = sumAllRemovalNutrition(opt.groups || [])
      for (const key of Object.keys(nested)) acc[key] = (acc[key] || 0) + nested[key]
    })
  })
  return acc
}

function addNutrition(base: any, add: Record<string, number>): any {
  if (!base) return base
  const result: any = { ...base }
  for (const key of Object.keys(add)) {
    if (typeof result[key] === 'number' || result[key] === undefined) {
      result[key] = (typeof result[key] === 'number' ? result[key] : 0) + add[key]
    }
  }
  return result
}

// Correct totalCals for the header calorie display
function correctGroupCals(groups: any[]): number {
  let total = 0
  groups?.forEach(g => {
    g.options?.forEach((opt: any) => {
      if (opt.quantity > 0 && opt.cals !== null && opt.cals !== undefined) {
        total += (isRemoval(opt.name) ? -opt.cals : opt.cals) * opt.quantity
      }
      total += correctGroupCals(opt.groups || [])
    })
  })
  return total
}

// Transform groups for display:
//  - Correct each option's `cals` label to its full configured total so a
//    section (e.g. a meal's sandwich) reflects BOTH its selected modifiers and
//    its "Remove" modifiers: base + Σ(selected modifiers) + Σ(unselected
//    removals); selecting a removal subtracts it back out. Same formula as the
//    item header. Leaf options (no sub-groups) are unchanged.
//  - Negate nutritionalInfo on selected removal options so calcNutrition (in
//    @open-tender/utils) subtracts instead of adds for the nutrition panel.
function correctGroupsForDisplay(groups: any[]): any[] {
  return (groups || []).map(g => ({
    ...g,
    options: (g.options || []).map((opt: any) => {
      const correctedCals =
        opt.cals === null || opt.cals === undefined
          ? opt.cals
          : opt.cals +
            sumAllRemovalCals(opt.groups || []) +
            correctGroupCals(opt.groups || [])
      if (opt.quantity > 0 && isRemoval(opt.name)) {
        return {
          ...opt,
          cals: correctedCals,
          nutritionalInfo: negateNutritionalInfo(opt.nutritionalInfo),
        }
      }
      return {
        ...opt,
        cals: correctedCals,
        groups: correctGroupsForDisplay(opt.groups || []),
      }
    }),
  }))
}

// A meal/combo is structurally an item whose options are themselves composite
// items (each carries its own sub-groups: the sandwich's bread/toppings, the
// side's size, etc.). Single-item modifiers are flat (a topping, a bread choice)
// and carry no sub-groups. This distinguishes a meal regardless of its base
// calorie, so a meal with a non-null base still routes through the component-sum
// branch. The meal total is the item's base calorie plus the sum of its selected
// components.
function hasCompositeOptions(groups: any[]): boolean {
  return (groups || []).some(g =>
    (g.options || []).some((opt: any) => (opt.groups || []).length > 0)
  )
}

// Sum the SELECTED option in each group only — no recursion into unselected
// alternatives. Used for the meal/combo total: each (already-corrected) option's
// `cals` folds in that component's own modifiers/removals, so the chosen
// components are simply added up. (A meal has no top-level removals; the isRemoval
// guard is defensive.)
function sumSelectedCals(groups: any[]): number {
  let total = 0
  ;(groups || []).forEach(g => {
    ;(g.options || []).forEach((opt: any) => {
      if (opt.quantity > 0 && opt.cals !== null && opt.cals !== undefined) {
        total += (isRemoval(opt.name) ? -opt.cals : opt.cals) * opt.quantity
      }
    })
  })
  return total
}

function baseNutritionValue(info: any, key: string): number {
  const val = info ? info[key] : undefined
  return typeof val === 'number' ? val : 0
}

// Per-nutrient sum across the meal's components, matching the kiosk's calcNutrition
// rule so FE and kiosk agree. serving_weight and calories_from_fat are not in
// @open-tender/utils' nutritionKeys, so calcNutrition never aggregates them for a
// meal; we sum them here and write them onto the meal's nutritionalInfo, where
// calcNutrition passes them through untouched. For each option: a normal option
// contributes (its own value + its nested components) × quantity; a "Remove X"
// option contributes the absolute value while NOT selected (the ingredient is
// present by default) and 0 once removed. Runs on the raw builtItem groups, not the
// removal-negated display groups, because the remove handling is done here.
function sumNutritionLikeKiosk(groups: any[], key: string): number {
  let total = 0
  ;(groups || []).forEach(g => {
    ;(g.options || []).forEach((opt: any) => {
      const isRemove = (opt.name || '').toLowerCase().startsWith('remove')
      const selected = (opt.quantity || 0) > 0
      const per =
        baseNutritionValue(opt.nutritionalInfo, key) +
        sumNutritionLikeKiosk(opt.groups || [], key)
      total += isRemove ? (selected ? 0 : Math.abs(per)) : per * (opt.quantity || 0)
    })
  })
  return total
}

export function correctRemovalModifierCals(builtItem: CartItem): CartItem {
  // Always correct the per-section option labels + nutrition-panel negation.
  const correctedGroups = correctGroupsForDisplay(builtItem.groups || [])

  // Meals/combos: total = the item's own base calorie + the sum of ONLY the
  // selected option in each group. Each option's corrected `cals` already folds in
  // that component's own modifiers + removals, so we just add up the chosen
  // components. We must NOT recurse into unselected alternatives: in a "choose one"
  // group the builder still marks every other option's default sub-modifier (e.g.
  // each shake's default add-on, each side's default sauce), and summing those
  // inflates the total with items the customer never picked. Updates on every
  // selection change.
  //
  // The base is added on top of the components: if the item carries a base calorie
  // it is included, and if it has none (cals null/0) only the components count.
  // `hasCompositeOptions` detects a meal (any item whose components are themselves
  // composite items) so a non-null base still routes here and is summed with the
  // components rather than taking the flat single-item path below.
  if (
    builtItem.cals === null ||
    builtItem.cals === undefined ||
    hasCompositeOptions(builtItem.groups || [])
  ) {
    const baseCals =
      builtItem.cals === null || builtItem.cals === undefined ? 0 : builtItem.cals
    const perItemCals = baseCals + sumSelectedCals(correctedGroups)
    const totalCals = builtItem.quantity * perItemCals

    // Keep the Nutritional Info panel in sync with this header total. The panel
    // renders calcNutritionalInfo(builtItem) = item-level nutrition + Σ selected
    // components (calcNutrition, recursive) — a SEPARATE calorie computation from
    // the header. Left alone it would not match perItemCals: it omits each
    // component's removal-by-default and runs its own component walk. Reuse
    // calcNutrition (the exact function the panel uses) to get the component sum,
    // then set the item-level calories so the panel lands on the same per-item
    // total: calories = perItemCals − Σ(component calories). Since perItemCals now
    // includes the base, the panel reflects base + components too. Updates on every
    // selection change.
    const componentCals =
      calcNutrition({
        nutritionalInfo: { calories: 0 },
        groups: correctedGroups,
      } as any).calories || 0
    // serving_weight and calories_from_fat are additive across components but are
    // not aggregated by calcNutrition (they're absent from its nutritionKeys), so
    // sum them here from the selected components and add the meal's own base.
    const baseInfo = builtItem.nutritionalInfo
    const aligned: Record<string, number> = {
      calories: perItemCals - componentCals,
      calories_from_fat:
        baseNutritionValue(baseInfo, 'calories_from_fat') +
        sumNutritionLikeKiosk(builtItem.groups || [], 'calories_from_fat'),
      serving_weight:
        baseNutritionValue(baseInfo, 'serving_weight') +
        sumNutritionLikeKiosk(builtItem.groups || [], 'serving_weight'),
    }

    // Align the remaining nutrients to the kiosk's present-removable rule. These
    // keys ARE in @open-tender/utils' nutritionKeys, so calcNutrition (run by the
    // panel via calcNutritionalInfo) re-aggregates them selected-only on top of
    // whatever base we set. Back-solve the base so base + calcNutrition's selected
    // sum lands on the kiosk total (base + present-removable sum) — the same trick
    // used for calories above. (calories is left to perItemCals, serving_weight /
    // calories_from_fat are set directly since calcNutrition ignores them.)
    const componentSums = calcNutrition({
      nutritionalInfo: {},
      groups: correctedGroups,
    } as any)
    const KIOSK_ALIGNED_KEYS = [
      'cholesterol',
      'dietary_fiber',
      'protein',
      'saturated_fat',
      'sodium',
      'sugars',
      'total_carbs',
      'total_fat',
      'trans_fat',
    ]
    for (const key of KIOSK_ALIGNED_KEYS) {
      const kioskTotal =
        baseNutritionValue(baseInfo, key) +
        sumNutritionLikeKiosk(builtItem.groups || [], key)
      aligned[key] = kioskTotal - (componentSums[key] || 0)
    }
    const nutritionalInfo = { ...baseInfo, ...aligned } as NutritionalInfo
    return { ...builtItem, nutritionalInfo, groups: correctedGroups, totalCals }
  }

  // Single items: removables are present by default, so bump the base by every
  // removal modifier's calories/nutrition; correctGroupCals / the negated groups
  // then subtract the ones the customer selects. Net = base + Σ(unselected removals).
  const removalBaseCals = sumAllRemovalCals(builtItem.groups || [])
  const removalBaseNutrition = sumAllRemovalNutrition(builtItem.groups || [])
  const bumpedCals = builtItem.cals + removalBaseCals
  const bumpedNutritionalInfo = addNutrition(
    (builtItem as any).nutritionalInfo,
    removalBaseNutrition
  )
  const optionCals = correctGroupCals(builtItem.groups || [])
  const totalCals = builtItem.quantity * (bumpedCals + optionCals)
  return {
    ...builtItem,
    cals: bumpedCals,
    nutritionalInfo: bumpedNutritionalInfo,
    groups: correctedGroups,
    totalCals,
  }
}
